// @spec NIS2-STR-028
// Route file for NIS2 Progress Updates page

import { createFileRoute, Link } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useApproveProgressUpdate,
  useAutoPopulateData,
  useCreateProgressUpdate,
  useDownloadProgressUpdatePdf,
  useProgressUpdate,
  useProgressUpdatesList,
  useSubmitProgressUpdate,
  useUpdateProgressUpdate,
} from '@/features/nis2/api/progress-update';
import type {
  CreateProgressUpdateRequest,
  SubmitProgressUpdateRequest,
} from '@/features/nis2/api/progress-update';
import { ProgressUpdateForm } from '@/features/nis2/components/ProgressUpdateForm';
import { ProgressUpdateList } from '@/features/nis2/components/ProgressUpdateList';
import { ProgressUpdatePreview } from '@/features/nis2/components/ProgressUpdatePreview';
import { ProgressUpdateSubmissionDialog } from '@/features/nis2/components/ProgressUpdateSubmissionDialog';
import type { ProgressUpdateFormData } from '@/features/nis2/schemas/progress-update-schema';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/nis2/incidents/$incidentId/progress-updates')(
  {
    beforeLoad: () => ({
      getTitle: () => i18n.t('routeTitles.nis2ProgressUpdates'),
    }),
    component: ProgressUpdatesRoute,
  }
);

type ViewMode = 'list' | 'create' | 'edit' | 'preview';

function ProgressUpdatesRoute() {
  const { t } = useTranslation('nis2');
  const { organizations } = useOrganizations();
  const activeOrg = organizations.find((org) => org.isActive);
  const { incidentId } = Route.useParams();

  const [viewMode, setViewMode] = React.useState<ViewMode>('list');
  const [selectedUpdateId, setSelectedUpdateId] = React.useState<string | null>(null);
  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = React.useState(false);

  // The incidentId from the URL is the nis2_incident_id
  const nis2IncidentId = incidentId;

  const orgId = activeOrg?.id ?? '';

  // Queries
  const listQuery = useProgressUpdatesList({
    orgId,
    nis2IncidentId,
    enabled: Boolean(orgId) && Boolean(nis2IncidentId),
  });

  const updateQuery = useProgressUpdate({
    orgId,
    nis2IncidentId,
    progressUpdateId: selectedUpdateId ?? '',
    enabled: Boolean(orgId) && Boolean(nis2IncidentId) && Boolean(selectedUpdateId),
  });

  const autoPopulateQuery = useAutoPopulateData({
    orgId,
    nis2IncidentId,
    enabled: Boolean(orgId) && Boolean(nis2IncidentId) && viewMode === 'create',
  });

  // Mutations
  const createMutation = useCreateProgressUpdate({
    orgId,
    nis2IncidentId,
  });

  const updateMutation = useUpdateProgressUpdate({
    orgId,
    nis2IncidentId,
    progressUpdateId: selectedUpdateId ?? '',
  });

  const approveMutation = useApproveProgressUpdate({
    orgId,
    nis2IncidentId,
    progressUpdateId: selectedUpdateId ?? '',
  });

  const submitMutation = useSubmitProgressUpdate({
    orgId,
    nis2IncidentId,
    progressUpdateId: selectedUpdateId ?? '',
  });

  const downloadPdfMutation = useDownloadProgressUpdatePdf({
    orgId,
    nis2IncidentId,
    progressUpdateId: selectedUpdateId ?? '',
    incidentNumber: updateQuery.data?.incident_details?.incident_number,
    sequenceNumber: updateQuery.data?.sequence_number,
  });

  if (!orgId) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('progressUpdates.configurationError')}</AlertTitle>
            <AlertDescription>
              {t('progressUpdates.orgConfigIncomplete')}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  function handleCreateNew() {
    setSelectedUpdateId(null);
    setViewMode('create');
  }

  function handleSelectUpdate(id: string) {
    setSelectedUpdateId(id);
    setViewMode('preview');
  }

  function handleCancel() {
    setSelectedUpdateId(null);
    setViewMode('list');
  }

  function handleEdit() {
    setViewMode('edit');
  }

  async function handleSave(data: ProgressUpdateFormData) {
    const request: CreateProgressUpdateRequest = {
      update_summary: data.update_summary,
      current_status: data.current_status,
      mitigation_update: data.mitigation_update,
      next_steps: data.next_steps,
      new_findings: data.new_findings ?? null,
      affected_systems_update: data.affected_systems_update ?? null,
      estimated_resolution: data.estimated_resolution ?? null,
      csirt_request_reference: data.csirt_request_reference ?? null,
      original_notification_id: data.original_notification_id ?? null,
    };

    try {
      if (viewMode === 'create') {
        const result = await createMutation.mutateAsync(request);
        toast.success(t('nis2:toast.progressUpdateCreated'), {
          description: t('progressUpdates.savedAsDraft', { number: String(result.sequence_number) }),
        });
        setSelectedUpdateId(result.id);
        setViewMode('preview');
      } else if (viewMode === 'edit' && selectedUpdateId) {
        await updateMutation.mutateAsync(request);
        toast.success(t('nis2:toast.progressUpdateSaved'), {
          description: t('progressUpdates.changesSaved'),
        });
        setViewMode('preview');
      }
    } catch {
      toast.error(t('nis2:toast.error'), {
        description: t('progressUpdates.failedToSave'),
      });
    }
  }

  async function handleApprove() {
    try {
      await approveMutation.mutateAsync();
      toast.success(t('nis2:toast.progressUpdateApproved'), {
        description: t('progressUpdates.readyForSubmission'),
      });
    } catch {
      toast.error(t('nis2:toast.error'), {
        description: t('progressUpdates.failedToApprove'),
      });
    }
  }

  function handleOpenSubmitDialog() {
    setIsSubmitDialogOpen(true);
  }

  async function handleSubmit(data: SubmitProgressUpdateRequest) {
    try {
      await submitMutation.mutateAsync(data);
      toast.success(t('nis2:toast.submissionRecorded'), {
        description: t('progressUpdates.markedAsSubmitted'),
      });
      setIsSubmitDialogOpen(false);
    } catch {
      toast.error(t('nis2:toast.error'), {
        description: t('progressUpdates.failedToRecordSubmission'),
      });
    }
  }

  function handleDownloadPdf() {
    downloadPdfMutation.mutate();
  }

  function renderContent() {
    switch (viewMode) {
      case 'list':
        return (
          <ProgressUpdateList
            nis2IncidentId={nis2IncidentId}
            items={listQuery.data?.items ?? []}
            isLoading={listQuery.isLoading}
            onCreateNew={handleCreateNew}
            onSelectUpdate={handleSelectUpdate}
          />
        );

      case 'create':
        return (
          <Card>
            <CardHeader>
              <CardTitle>{t('progressUpdates.createTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <ProgressUpdateForm
                nis2IncidentId={nis2IncidentId}
                originalNotifications={autoPopulateQuery.data?.original_notifications ?? []}
                autoPopulateData={autoPopulateQuery.data ?? null}
                onSave={(data) => {
                  void handleSave(data);
                }}
                onCancel={handleCancel}
                isSubmitting={createMutation.isPending}
              />
            </CardContent>
          </Card>
        );

      case 'edit':
        return (
          <Card>
            <CardHeader>
              <CardTitle>{t('progressUpdates.editTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <ProgressUpdateForm
                nis2IncidentId={nis2IncidentId}
                existingUpdate={updateQuery.data ?? null}
                originalNotifications={autoPopulateQuery.data?.original_notifications ?? []}
                onSave={(data) => {
                  void handleSave(data);
                }}
                onCancel={() => {
                  setViewMode('preview');
                }}
                isSubmitting={updateMutation.isPending}
              />
            </CardContent>
          </Card>
        );

      case 'preview':
        if (!updateQuery.data?.content) {
          return (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                {updateQuery.isLoading ? t('progressUpdates.loading') : t('progressUpdates.notFound')}
              </CardContent>
            </Card>
          );
        }
        return (
          <>
            <Button variant="ghost" className="mb-4 gap-2" onClick={handleCancel}>
              <ArrowLeft className="h-4 w-4" />
              {t('progressUpdates.backToList')}
            </Button>
            <ProgressUpdatePreview
              content={updateQuery.data.content}
              incidentNumber={updateQuery.data.incident_details?.incident_number ?? 'Unknown'}
              sequenceNumber={updateQuery.data.sequence_number}
              status={updateQuery.data.status}
              originalNotification={updateQuery.data.original_notification ?? null}
              onEdit={handleEdit}
              onApprove={() => {
                void handleApprove();
              }}
              onSubmit={handleOpenSubmitDialog}
              onDownloadPdf={handleDownloadPdf}
              isApproving={approveMutation.isPending}
              isDownloading={downloadPdfMutation.isPending}
            />
            <ProgressUpdateSubmissionDialog
              open={isSubmitDialogOpen}
              onOpenChange={setIsSubmitDialogOpen}
              onSubmit={(data) => {
                void handleSubmit(data);
              }}
              isSubmitting={submitMutation.isPending}
            />
          </>
        );

      default:
        return null;
    }
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Back navigation */}
        {viewMode === 'list' && (
          <Button variant="ghost" asChild className="gap-2">
            <Link to="/nis2/dashboard">
              <ArrowLeft className="h-4 w-4" />
              {t('progressUpdates.backToDashboard')}
            </Link>
          </Button>
        )}

        {renderContent()}
      </div>
    </div>
  );
}
